import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ActivityPanel, { ActivityItem } from "./ActivityPanel";
import ContactSearch from "./ContactSearch";
import CallHistoryPanel from "./CallHistoryPanel";
import HeroCard from "./HeroCard";
import EmbeddedSoftphonePanel from "./EmbeddedSoftphonePanel";
import { SoftphoneSipConfig, UserPermissions } from "../hooks/useSoftphoneConfig";
import { useScreenWakeLock, isScreenWakeLockSupported } from "../hooks/useScreenWakeLock";
import { useVersionCheck } from "../hooks/useVersionCheck";
import { useSessionPoll, SessionChangeReason } from "../hooks/useSessionPoll";
import type { SessionPollResult } from "../hooks/useAppBootstrapFlow";
import {
  useMediaDeviceWatcher,
  isMediaDeviceWatchSupported,
  deviceKey,
  MediaDeviceChange,
  MediaDeviceEntry,
} from "../hooks/useMediaDeviceWatcher";
import MediaDevicesPanel, { MediaDevicesState } from "./MediaDevicesPanel";
import { useTranslation } from "../i18n/LocaleContext";
import { SupportedLocale, resolveSoftphoneLanguage } from "../i18n/locale";
import { getRuntimeEnv } from "../config/runtimeEnv";
import { debugLog, debugWarn } from "../config/debug";
import { enqueue as enqueueClientLog, setContext as setClientLogContext } from "../debug/clientLog";
import AppFooter from "./AppFooter";
import WorkspaceBottomNav, { PanelId } from "./WorkspaceBottomNav";
import TenantAdminScreen, { TenantAdminCallState } from "./TenantAdminScreen";
import type { AuthorizedFetch } from "../utils/tenantAdminApi";
import "./SoftphoneDashboard.css";

const envConfig = {
  softphoneUrl: getRuntimeEnv("REACT_APP_SOFTPHONE_URL") || "/softphone/",
  softphoneOrigin: getRuntimeEnv("REACT_APP_SOFTPHONE_ALLOWED_ORIGIN") || "*",
  autoRegister: getRuntimeEnv("REACT_APP_SOFTPHONE_AUTO_REGISTER") === "true",
};

const nodeEnv = getRuntimeEnv("REACT_APP_NODE_ENV");
const showEventsPanel = !nodeEnv || nodeEnv === "dev";

type SoftphoneMessage = {
  event?: string;
  data?: any;
};

function createActivity(
  title: string,
  detail: string,
  locale: SupportedLocale,
  tone: ActivityItem["tone"] = "neutral",
  action?: ActivityItem["action"]
): ActivityItem {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    title,
    detail,
    tone,
    timestamp: new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }),
    action,
  };
}

interface SoftphoneWorkspaceProps {
  softphoneSipConfig: SoftphoneSipConfig | null;
  pabxApiToken: string | null;
  permissions: UserPermissions | null;
  sendAppEventNotification?: (event: { type: string; payload?: unknown }) => Promise<void>;
  /** Heartbeat da sessão (useSessionPoll); ausente quando o backend não devolveu `pollIntervalSeconds`. */
  sendSessionPoll?: () => Promise<SessionPollResult>;
  pollIntervalSeconds?: number | null;
  /** Usuário administra os usuários do tenant: habilita o botão do hero e a vista de administração. */
  tenantAdminAccess?: boolean;
  /** oid do usuário autenticado, para a tela de administração proteger a linha dele. */
  currentUserId?: string;
  /** Requisição autenticada do bootstrap (token SSO + repetição no 401), usada pela tela de administração. */
  authorizedFetch?: AuthorizedFetch;
}

function buildSoftphoneIframeUrl(
  softphoneSipConfig: SoftphoneSipConfig | null,
  locale: SupportedLocale
): string {
  if (typeof window === "undefined" || !softphoneSipConfig) {
    return "about:blank";
  }

  const baseUrl = envConfig.softphoneUrl.trim() || "/softphone/";
  const iframeUrl = new URL(baseUrl, window.location.origin);
  iframeUrl.search = "";
  iframeUrl.hash = "";
  iframeUrl.searchParams.set("user", softphoneSipConfig.extension || "");
  iframeUrl.searchParams.set("password", softphoneSipConfig.password || "");
  iframeUrl.searchParams.set("port", softphoneSipConfig.port || "");
  iframeUrl.searchParams.set("register", String(Boolean(softphoneSipConfig.register)));
  iframeUrl.searchParams.set("fullscreen", "true");
  iframeUrl.searchParams.set("server", softphoneSipConfig.domain || "");
  iframeUrl.searchParams.set("name", softphoneSipConfig.displayName || softphoneSipConfig.extension || "");
  iframeUrl.searchParams.set("logger", softphoneSipConfig.enableDebug ? "true" : "false");
  if (softphoneSipConfig.registerExpires != null) {
    iframeUrl.searchParams.set("expires", String(softphoneSipConfig.registerExpires));
  }
  iframeUrl.searchParams.set("disablesettings", String(Boolean(softphoneSipConfig.disableSettings)));
  // Sem `video`: a configuração de câmera é do softphone, que a mantém no próprio
  // localStorage. O shell não opina — nem para habilitar, nem para desabilitar.
  iframeUrl.searchParams.set("language", resolveSoftphoneLanguage(locale));
  return iframeUrl.toString();
}

export default function SoftphoneWorkspace({
  softphoneSipConfig,
  pabxApiToken,
  permissions,
  sendAppEventNotification,
  sendSessionPoll,
  pollIntervalSeconds = null,
  tenantAdminAccess = false,
  currentUserId = "",
  authorizedFetch,
}: SoftphoneWorkspaceProps) {
  const { t, locale } = useTranslation();
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  // Painel visível no layout mobile (≤720px). Acima disso a CSS ignora o atributo e os
  // três painéis aparecem lado a lado, então o valor fica inerte.
  const [activePanel, setActivePanel] = useState<PanelId>("softphone");
  // Espelha o último evento `presence` do softphone: busy = chamada em curso. É o que
  // mantém a tela ligada (useScreenWakeLock) para o WebView do Teams no Android não ser
  // suspenso no meio da chamada, cortando o áudio.
  const [softphoneBusy, setSoftphoneBusy] = useState(false);
  // Vista corrente: o softphone e seus painéis, ou a administração de usuários do tenant. A troca
  // é por CSS — nada do workspace é desmontado, para o iframe do softphone não perder o registro
  // SIP nem a chamada em curso (ver .softphone-shell[data-active-view] em SoftphoneDashboard.css).
  const [activeView, setActiveView] = useState<"workspace" | "tenantAdmin">("workspace");
  // Estado da chamada para a faixa da tela de administração. Vem do `callevent` quando o softphone
  // o emite (número de quem liga, tocando x atendida) e degrada para o `presence`, que já chega no
  // ring, no build que não tem callevent.
  const [callState, setCallState] = useState<TenantAdminCallState>({ kind: "idle" });
  // Sonda de permissão do EmbeddedSoftphonePanel concluída: antes disso enumerateDevices()
  // devolve os dispositivos sem `label`, e o snapshot inicial não diria nada.
  const [mediaGranted, setMediaGranted] = useState(false);
  // Lista corrente de dispositivos, alimentada pelo watcher e exibida no MediaDevicesPanel.
  const [devicesState, setDevicesState] = useState<MediaDevicesState | null>(null);
  // contentWindow do iframe do softphone. Precisa ser estado, não ref: o watcher assina o
  // `navigator.mediaDevices` daquele realm e tem de re-assinar quando o iframe (re)carrega.
  const [softphoneWindow, setSoftphoneWindow] = useState<Window | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const activityStatusRef = useRef<string | null>(null);
  const historyRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const softphoneOrigin = envConfig.softphoneOrigin || "*";
  const softphoneUrl = useMemo(
    () => buildSoftphoneIframeUrl(softphoneSipConfig, locale),
    [softphoneSipConfig, locale]
  );

  const bootstrapPayload = useMemo(() => {
    if (!softphoneSipConfig) return null;

    return {
      username: softphoneSipConfig.extension,
      extension: softphoneSipConfig.extension,
      domain: softphoneSipConfig.domain,
      password: softphoneSipConfig.password,
      registrar: softphoneSipConfig.domain,
      displayName: softphoneSipConfig.displayName,
      autoRegister: envConfig.autoRegister,
    };
  }, [softphoneSipConfig]);

  // O check-in decide se as linhas de console são enviadas: `enableDebug` é o mesmo campo que
  // vira `logger=` na URL do iframe, administrado por usuário no msteams-ui. Desligado, o
  // módulo descarta a fila do boot e devolve o console ao original.
  useEffect(() => {
    setClientLogContext({
      extension: softphoneSipConfig?.extension,
      enabled: Boolean(softphoneSipConfig?.enableDebug),
    });
  }, [softphoneSipConfig?.extension, softphoneSipConfig?.enableDebug]);

  useEffect(() => {
    const currentStatusKey = softphoneSipConfig?.extension || "";
    if (activityStatusRef.current === currentStatusKey) return;
    activityStatusRef.current = currentStatusKey;

    if (softphoneSipConfig) {
      setActivity((current) =>
        [
          createActivity(
            t("softphoneWorkspace.activity.configSynced.title"),
            t("softphoneWorkspace.activity.configSynced.detail", { extension: softphoneSipConfig.extension }),
            locale,
            "good"
          ),
          ...current,
        ].slice(0, 8)
      );
    }
  }, [softphoneSipConfig, t, locale]);

  const postToSoftphone = useCallback(
    (type: string, payload: Record<string, unknown>) => {
      if (!iframeRef.current?.contentWindow) return;
      iframeRef.current.contentWindow.postMessage({ event: type, data: payload }, softphoneOrigin );
    },
    [softphoneOrigin]
  );

  // Origem de chamada: o softphone espera { event: 'call', data: '<numero>', variables }
  // (data e a string do numero; variables e irmao, opcional -> header X-uTech-CTIWEB).
  // Por isso nao usamos postToSoftphone, que aninha tudo sob `data`.
  const placeCall = useCallback(
    (number: string, variaveis = "") => {
      if (!iframeRef.current?.contentWindow) return;
      const sanitized = (number || "").replace(/[^0-9+*#]/g, "");
      if (!sanitized) return;
      iframeRef.current.contentWindow.postMessage(
        { event: "call", data: sanitized, variables: variaveis },
        softphoneOrigin
      );
      // A chamada foi disparada de Contatos ou do Histórico (ambos passam por aqui só
      // depois da confirmação no diálogo, via useMakeCall). No mobile o softphone está
      // escondido atrás da aba de origem, então trazemos ele à frente — é lá que o
      // usuário acompanha e encerra a chamada.
      setActivePanel("softphone");
    },
    [softphoneOrigin]
  );

  // Handler de mensagens vindas do softphone (ou do mock).
  const handleSoftphoneMessage = useCallback(
    (event: MessageEvent<SoftphoneMessage>) => {
      if (softphoneOrigin !== "*" && event.origin !== softphoneOrigin) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;

      // Antes do debugLog: em build de dev ele chama console.log, que está capturado, então
      // cada linha vinda do softphone geraria uma linha extra do shell.
      if (data.event === "console") {
        // O `ts` vem de dentro do iframe, do instante do `console.*`. Sem repassá-lo, a hora
        // gravada seria a da entrega deste postMessage — uma rajada emitida num único tick
        // chega em tasks separadas e sairia achatada contra as linhas do shell.
        enqueueClientLog("softphone", data.data?.level, data.data?.message ?? "", data.data?.ts);
        return;
      }

      debugLog("SoftphoneWorkspace received message:", event.data);

      if (data.event === "softphone.ready") {
        if (bootstrapPayload) postToSoftphone("softphone.bootstrap", bootstrapPayload);
        setActivity((current) =>
          [createActivity(t("softphoneWorkspace.activity.connected.title"), t("softphoneWorkspace.activity.connected.detail"), locale, "good"), ...current].slice(0, 8)
        );
      }

      if (data.event === "softphone.incomingCall") {
        const caller = data.data?.from || t("softphoneWorkspace.activity.unknownCaller");
        setActivity((current) =>
          [createActivity(t("softphoneWorkspace.activity.incomingCall.title"), t("softphoneWorkspace.activity.incomingCall.detail", { caller }), locale, "warning", "answer"), ...current].slice(0, 8)
        );
      }

      if (data.event === "softphone.outgoingCall") {
        const target = data.data?.to || t("softphoneWorkspace.activity.unknownCaller");
        setActivity((current) =>
          [createActivity(t("softphoneWorkspace.activity.outgoingCall.title"), t("softphoneWorkspace.activity.outgoingCall.detail", { target }), locale, "warning", "answer"), ...current].slice(0, 8)
        );
      }

      if (data.event === "softphone.ringing") {
        setActivity((current) =>
          [createActivity(t("softphoneWorkspace.activity.ringing.title"), t("softphoneWorkspace.activity.ringing.detail"), locale, "warning"), ...current].slice(0, 8)
        );
      }

      if (data.event === "softphone.callAnswered") {
        const target = data.data?.remote || data.data?.target || data.data?.from || undefined;
        setActivity((current) =>
          [createActivity(
            t("softphoneWorkspace.activity.answered.title"),
            target
              ? t("softphoneWorkspace.activity.answered.detailWithTarget", { target })
              : t("softphoneWorkspace.activity.answered.detailNoTarget"),
            locale,
            "good"
          ), ...current].slice(0, 8)
        );
      }

      if (data.event === "softphone.hangup") {
        const target = data.data?.remote || data.data?.target || data.data?.from || undefined;
        setActivity((current) =>
          [createActivity(
            t("softphoneWorkspace.activity.hangup.title"),
            target
              ? t("softphoneWorkspace.activity.hangup.detailWithTarget", { target })
              : t("softphoneWorkspace.activity.hangup.detailNoTarget"),
            locale,
            "neutral"
          ), ...current].slice(0, 8)
        );
      }

      if (data.event === "softphone.notification") {
        setActivity((current) =>
          [createActivity(
            data.data?.title || t("softphoneWorkspace.activity.notification.defaultTitle"),
            data.data?.detail || t("softphoneWorkspace.activity.notification.defaultDetail"),
            locale,
            data.data?.tone || "neutral",
            data.data?.action
          ), ...current].slice(0, 8)
        );
      }

      // Estado da chamada (1 calling, 2 proceeding, 3 answered, 4 released), repassado como veio.
      // Mesmo catch do presence: uma falha não pode virar unhandled rejection no log remoto.
      if (data.event === "callevent") {
        // Alimenta a faixa da tela de administração. O `state` 4 (released) pode chegar duas
        // vezes — a desmontagem do painel reemite o último payload —, então zerar é idempotente.
        const callEventState = Number(data.data?.state);
        if (callEventState === 1 || callEventState === 2) {
          setCallState({
            kind: "ringing",
            from: data.data?.direction === "incoming" ? data.data?.calling : undefined,
          });
        } else if (callEventState === 3) {
          setCallState({ kind: "active", detailed: true });
        } else if (callEventState === 4) {
          setCallState({ kind: "idle" });
        }
        void sendAppEventNotification?.({ type: "callevent", payload: data.data }).catch(() => {});
      }

      if (data.event === "presence") {
        const state = data.data?.state || "unknown";
        // console.log direto, e não debugLog: presence é o sinal mais direto de "a chamada
        // terminou", e é em produção (build sem debugLog) que ele precisa aparecer no log remoto.
        console.log(`[presence] state=${state}`);
        setSoftphoneBusy(state === "busy");
        // Fallback para o build sem `callevent`: `busy` já chega no ring (sessão em NEW), então a
        // faixa aparece em tempo de atender — só sem distinguir "tocando" de "em chamada" nem
        // saber quem liga. Um callevent posterior refina isto; aqui o `detailed: false` é o que
        // escolhe o texto genérico.
        setCallState((current) => {
          if (state === "idle") return { kind: "idle" };
          if (state === "busy" && current.kind === "idle") return { kind: "active", detailed: false };
          return current;
        });
        setActivity((current) =>
          [createActivity(t("softphoneWorkspace.activity.presence.title"), t("softphoneWorkspace.activity.presence.detail", { state }), locale, "neutral"), ...current].slice(0, 8)
        );
        // Presença é best-effort e o envio agora lança em resposta não-OK; sem este catch, uma
        // falha viraria unhandled rejection — que o captureConsole escuta e transformaria em mais
        // uma linha de log, que geraria outro POST.
        void sendAppEventNotification?.({ type: "presence", payload: data.data }).catch(() => {});

        // Atualiza o histórico de chamadas 3s após o evento, apenas quando o
        // softphone fica idle. Se já houver um refresh agendado, ignora eventos
        // subsequentes (não reinicia o timer).
        if (state === "idle" && historyRefreshTimerRef.current === null) {
          historyRefreshTimerRef.current = setTimeout(() => {
            historyRefreshTimerRef.current = null;
            setHistoryRefreshKey((key) => key + 1);
          }, 3000);
        }
      }
    },
    [bootstrapPayload, postToSoftphone, sendAppEventNotification, softphoneOrigin, t, locale]
  );

  const pushWakeLockActivity = useCallback(
    (detail: string, tone: ActivityItem["tone"]) => {
      setActivity((current) =>
        [createActivity(t("softphoneWorkspace.activity.wakeLock.title"), detail, locale, tone), ...current].slice(0, 8)
      );
    },
    [t, locale]
  );

  // Polling de /version. Mora aqui, e não no App, porque o workspace só monta depois do
  // check-in 200 — a primeira consulta (o baseline) sai logo após ele. Só escreve estado
  // próprio, então é seguro no caminho de resume da suspensão.
  const { currentVersion, latestVersion } = useVersionCheck(
    useCallback(
      (current: string, latest: string) => {
        setActivity((existing) =>
          [
            createActivity(
              t("softphoneWorkspace.activity.version.title"),
              t("softphoneWorkspace.activity.version.detail", { current, latest }),
              locale,
              "warning"
            ),
            ...existing,
          ].slice(0, 8)
        );
      },
      [t, locale]
    )
  );

  // Heartbeat da sessão + detecção de configuração alterada. Mora aqui pelas mesmas razões do
  // /version acima: só existe depois do check-in 200 e escreve só estado próprio.
  const { reloadPending: sessionReloadPending, reloadReason: sessionReloadReason } = useSessionPoll({
    enabled: Boolean(sendSessionPoll),
    intervalSeconds: pollIntervalSeconds,
    snapshot: useMemo(() => ({ sip: softphoneSipConfig, permissions }), [softphoneSipConfig, permissions]),
    poll: sendSessionPoll ?? (() => Promise.reject(new Error("sessionPoll indisponível"))),
    onChangeDetected: useCallback(
      (reason: SessionChangeReason) => {
        setActivity((existing) =>
          [
            createActivity(
              t("softphoneWorkspace.activity.session.title"),
              t("softphoneWorkspace.activity.session.detail", { reason: t(`softphoneWorkspace.sessionReason.${reason}`) }),
              locale,
              "warning"
            ),
            ...existing,
          ].slice(0, 8)
        );
      },
      [t, locale]
    ),
  });

  // O único reload automático da aplicação, para os dois gatilhos: configuração alterada
  // (sessão) e bundle novo publicado (/version). Só com o softphone livre: recarregar destrói o
  // iframe, e com ele uma chamada em curso. `softphoneBusy` começa em false — antes do primeiro
  // `presence` não há chamada a preservar. Não escreve softphoneConfig/registrationStatus: o
  // documento novo passa pelo check-in e recebe a configuração vigente.
  const reloadReason = sessionReloadPending ? `session:${sessionReloadReason}` : latestVersion ? `version:${latestVersion}` : null;
  useEffect(() => {
    if (!reloadReason || softphoneBusy) return;
    console.log(`[reload] recarregando: ${reloadReason}`);
    const timer = setTimeout(() => window.location.reload(), 1500);
    return () => clearTimeout(timer);
  }, [reloadReason, softphoneBusy]);

  // O painel recua para áudio quando o vídeo está habilitado mas a câmera não pode ser
  // aberta. Sem registrar isso, o usuário perderia o vídeo silenciosamente.
  const pushMediaActivity = useCallback(
    (detail: string) => {
      setActivity((current) =>
        [createActivity(t("softphoneWorkspace.activity.media.title"), detail, locale, "warning"), ...current].slice(0, 8)
      );
    },
    [t, locale]
  );

  // Mantém a tela ligada enquanto o softphone estiver busy. Os eventos vão para o painel
  // de eventos porque é a única forma prática de verificar, no próprio aparelho, se o
  // WebView do Teams delega a permission policy screen-wake-lock para a tab.
  useScreenWakeLock(softphoneBusy, {
    onAcquired: () => pushWakeLockActivity(t("softphoneWorkspace.activity.wakeLock.acquired"), "good"),
    onReleased: () => pushWakeLockActivity(t("softphoneWorkspace.activity.wakeLock.released"), "neutral"),
    onError: (reason: string) => pushWakeLockActivity(t("softphoneWorkspace.activity.wakeLock.failed", { reason }), "warning"),
  });

  useEffect(() => {
    pushWakeLockActivity(
      isScreenWakeLockSupported()
        ? t("softphoneWorkspace.activity.wakeLock.supported")
        : t("softphoneWorkspace.activity.wakeLock.unsupported"),
      isScreenWakeLockSupported() ? "neutral" : "warning"
    );
    // Só no mount: é um diagnóstico de plataforma, não muda em runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pushDeviceActivity = useCallback(
    (detail: string, tone: ActivityItem["tone"]) => {
      setActivity((current) =>
        [createActivity(t("softphoneWorkspace.activity.devices.title"), detail, locale, tone), ...current].slice(0, 8)
      );
    },
    [t, locale]
  );

  // Observa entradas e saídas de mídia. É instrumentação: serve para confirmar no próprio
  // aparelho o que cada sistema operacional expõe — em particular que um headset com fio não
  // gera dispositivo novo (é um port do mesmo sink), ao contrário de Bluetooth e USB.
  const handleDeviceChange = useCallback(
    (change: MediaDeviceChange) => {
      setDevicesState({
        devices: change.devices,
        addedKeys: change.added.map(deviceKey),
        anonymized: change.anonymized,
        trigger: change.trigger,
        enumeratedFrom: change.enumeratedFrom,
        timestamp: new Date().toLocaleTimeString(locale, {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      });

      if (change.anonymized) {
        pushDeviceActivity(t("softphoneWorkspace.activity.devices.anonymized"), "warning");
        return;
      }

      if (change.initial) {
        pushDeviceActivity(
          t("softphoneWorkspace.activity.devices.initial", { count: change.devices.length }),
          "neutral"
        );
        return;
      }
      // Resumo curto: a lista completa fica no console. O painel guarda só 8 itens e não pode
      // ser inundado pelas trocas de dispositivo, que expulsariam os eventos de chamada.
      // Uma frase por categoria, com os dispositivos agrupados dentro dela — um headset
      // registra entrada e saída de uma vez, e duas linhas repetindo o verbo seriam ruído.
      const describe = (device: MediaDeviceEntry) =>
        `${device.label || t("mediaDevicesPanel.noLabel")} (${device.kind})`;
      const parts: string[] = [];
      if (change.added.length > 0) {
        parts.push(
          t("softphoneWorkspace.activity.devices.added", { devices: change.added.map(describe).join(", ") })
        );
      }
      if (change.removed.length > 0) {
        parts.push(
          t("softphoneWorkspace.activity.devices.removed", { devices: change.removed.map(describe).join(", ") })
        );
      }
      if (change.defaultChanged.length > 0) {
        parts.push(
          t("softphoneWorkspace.activity.devices.defaultChanged", {
            devices: change.defaultChanged.map(describe).join(", "),
          })
        );
      }
      const summary = parts.join(" · ");
      pushDeviceActivity(
        t("softphoneWorkspace.activity.devices.changed", { summary }),
        change.added.length > 0 ? "good" : "neutral"
      );
    },
    [pushDeviceActivity, t, locale]
  );

  const { refresh: refreshDevices } = useMediaDeviceWatcher(mediaGranted, {
    softphoneWindow,
    onChange: handleDeviceChange,
    onError: (reason: string) =>
      pushDeviceActivity(t("softphoneWorkspace.activity.devices.failed", { reason }), "warning"),
    onPermissionState: (state: string) =>
      pushDeviceActivity(
        t("softphoneWorkspace.activity.devices.permission", { state }),
        state === "granted" ? "neutral" : "warning"
      ),
  });

  useEffect(() => {
    pushDeviceActivity(
      isMediaDeviceWatchSupported()
        ? t("softphoneWorkspace.activity.devices.supported")
        : t("softphoneWorkspace.activity.devices.unsupported"),
      isMediaDeviceWatchSupported() ? "neutral" : "warning"
    );
    // Só no mount: é um diagnóstico de plataforma, não muda em runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpa o timer de refresh do histórico ao desmontar.
  useEffect(
    () => () => {
      if (historyRefreshTimerRef.current) clearTimeout(historyRefreshTimerRef.current);
    },
    []
  );

  // Wrapper de identidade estável que sempre chama a versão mais recente do
  // handler — necessário para anexar/remover listeners de forma confiável.
  const handlerRef = useRef(handleSoftphoneMessage);
  useEffect(() => {
    handlerRef.current = handleSoftphoneMessage;
  }, [handleSoftphoneMessage]);
  const stableHandler = useCallback((event: MessageEvent) => {
    handlerRef.current(event as MessageEvent<SoftphoneMessage>);
  }, []);

  // Listener no window do app: captura o mock (que faz window.parent.postMessage).
  useEffect(() => {
    window.addEventListener("message", stableHandler);
    return () => window.removeEventListener("message", stableHandler);
  }, [stableHandler]);

  // O softphone original chama window.postMessage(...), entregando o evento à
  // PRÓPRIA janela do iframe (não ao pai). Como app e softphone são same-origin
  // (mesmo host via nginx), anexamos o handler dentro do documento do iframe.
  //
  // Não basta anexar no onLoad: o softphone registra e pode receber o INVITE antes
  // do load do iframe. O callevent de "tocando" emitido nessa janela se perde (só o
  // listener já anexado o recebe) e a notificação de chamada entrante nunca sai.
  // Por isso anexamos assim que cada documento novo aparece (ver o effect abaixo).
  // O contentWindow do iframe é o mesmo WindowProxy entre navegações; o que muda é o
  // Document, então é nele que observamos a troca.
  const attachedIframeWindowRef = useRef<Window | null>(null);
  const attachedIframeDocRef = useRef<Document | null>(null);
  const attachSoftphoneListener = useCallback((): Document | null => {
    const win = iframeRef.current?.contentWindow ?? null;
    if (!win) return null;
    let doc: Document;
    try {
      doc = win.document;
    } catch {
      // contentWindow inacessível (cross-origin) — não há como capturar via app.
      debugWarn("Could not access the softphone contentWindow (cross-origin?).");
      return null;
    }
    if (doc === attachedIframeDocRef.current) return doc;
    if (attachedIframeWindowRef.current) {
      try {
        attachedIframeWindowRef.current.removeEventListener("message", stableHandler);
      } catch {
        // documento antigo já descarregado — nada a remover
      }
    }
    // O watcher de dispositivos também precisa deste realm — ele assina o devicechange do
    // navigator.mediaDevices de lá, que é onde o softphone tem captura ativa.
    setSoftphoneWindow(win);
    try {
      win.addEventListener("message", stableHandler);
    } catch {
      debugWarn("Could not access the softphone contentWindow (cross-origin?).");
      return null;
    }
    attachedIframeWindowRef.current = win;
    attachedIframeDocRef.current = doc;
    console.log(`[callevent] listener anexado ao documento do softphone (readyState=${doc.readyState}, url=${doc.URL})`);
    return doc;
  }, [stableHandler]);

  const handleIframeLoad = useCallback(() => {
    attachSoftphoneListener();
  }, [attachSoftphoneListener]);

  // Observa o iframe para anexar o listener no documento do softphone logo após o commit
  // da navegação, antes de o bundle rodar. Enquanto o iframe não existe (permissão de mídia
  // pendente) ou o documento é o about:blank inicial, a checagem é a cada 10 ms ou 100 ms; com
  // o documento do softphone já completo, a cada 1 s, só para pegar uma remontagem do iframe.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const watch = () => {
      const doc = attachSoftphoneListener();
      let delay = 100;
      if (doc) {
        const loaded = doc.readyState === "complete" && doc.URL !== "about:blank";
        delay = loaded ? 1000 : 10;
      }
      timer = setTimeout(watch, delay);
    };
    watch();
    return () => clearTimeout(timer);
  }, [attachSoftphoneListener]);

  function sendCommand(type: string, payload: Record<string, unknown> = {}) {
    if (!bootstrapPayload) return;

    postToSoftphone(type, payload);
    setActivity((current) =>
      [createActivity(t("softphoneWorkspace.activity.commandSent.title"), t("softphoneWorkspace.activity.commandSent.detail", { type }), locale, "good"), ...current].slice(0, 8)
    );
  }

  function openSoftphoneFromNotification(item: ActivityItem) {
    // No mobile o softphone pode estar escondido atrás de outra aba — sem isto o clique
    // na notificação para atender não produziria nada visível.
    setActivePanel("softphone");
    iframeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    iframeRef.current?.contentWindow?.focus();
    if (item.action === "answer") sendCommand("softphone.answerCall");
  }

  return (
    <div className="softphone-shell" data-active-view={activeView}>
      <HeroCard
        sipConfig={softphoneSipConfig}
        currentVersion={currentVersion}
        isTenantAdmin={tenantAdminAccess}
        tenantAdminActive={activeView === "tenantAdmin"}
        onToggleTenantAdmin={() =>
          setActiveView((current) => (current === "tenantAdmin" ? "workspace" : "tenantAdmin"))
        }
      />

      {/* Desmontado ao sair, ao contrário do softphone — e por motivo oposto ao dele. O painel
          carrega o diretório inteiro da organização, e segurá-lo pelo resto da vida do documento
          trabalharia contra o orçamento de memória que decide quando o Teams descarrega a
          aplicação suspensa (ver "App Suspension" no CLAUDE.md). O custo é recarregar a lista na
          próxima visita; o do softphone seria derrubar a chamada, que é incomparável. */}
      {activeView === "tenantAdmin" && authorizedFetch && (
        <TenantAdminScreen
          authorizedFetch={authorizedFetch}
          currentUserId={currentUserId}
          onBack={() => setActiveView("workspace")}
          callState={callState}
          onGoToSoftphone={() => {
            setActiveView("workspace");
            setActivePanel("softphone");
          }}
        />
      )}

      <section className="dashboard-grid" data-active-panel={activePanel}>
        <EmbeddedSoftphonePanel
          iframeRef={iframeRef}
          softphoneUrl={softphoneUrl}
          bootstrapPayload={bootstrapPayload}
          postToSoftphone={postToSoftphone}
          onIframeLoad={handleIframeLoad}
          onMediaNotice={pushMediaActivity}
          onMediaPermission={setMediaGranted}
          className="panel-softphone"
        />

        <ContactSearch sipConfig={softphoneSipConfig} utechApiToken={pabxApiToken ?? ""} permissions={permissions} onPlaceCall={placeCall} authorizedFetch={authorizedFetch} className="panel-contacts" />

        <CallHistoryPanel sipConfig={softphoneSipConfig} utechApiToken={pabxApiToken ?? ""} permissions={permissions} onPlaceCall={placeCall} refreshKey={historyRefreshKey} className="panel-history" />
      </section>

      {showEventsPanel && (
        <section className="dashboard-secondary-row" data-active-panel={activePanel}>
          <ActivityPanel
            activity={activity}
            onClear={() => setActivity([])}
            onNotificationClick={openSoftphoneFromNotification}
            className="panel-events"
          />

          <MediaDevicesPanel state={devicesState} onRefresh={refreshDevices} className="panel-events" />
        </section>
      )}

      <AppFooter />

      <WorkspaceBottomNav value={activePanel} onChange={setActivePanel} showEvents={showEventsPanel} />
    </div>
  );
}